import { Component, h, createRef, RefObject } from 'preact';
import cx from 'classnames';

import { VlPropertySelectionHeader } from './vl-property-selection-header';
import { InqPropertyCard } from '../propcard/inq-property-card.component';
import { ViewingList, ViewingListPropertyGroup } from 'inquiries/viewing-list.model';
import { VlPropertySearchResponse } from 'inquiries/vl-property-search-response.model';
import { Listing } from 'listing.model';

interface VlPropertySelectionPanelProps {
  onOpenFinalList: () => void;
  onSearch?: () => void;
  onClose: () => void;
  onViewFtf?: () => void;
  onPageChange?: (page: number) => void;

  propertySearchResults?: VlPropertySearchResponse;

  onAddToList: (listing: Listing, fromGroup: string) => void;
  viewingList: ViewingList;
}

interface VlPropertySelectionPanelState {
  currentPage?: number;
  searchScrollPos?: number;
  searchHiddenByScroll?: boolean;
}

const VIEWING_LIST_PROPERTY_SELECTION_GROUPS = [
  { key: 'clientSelected', label: 'Client Selected Listings' },
  { key: 'myList', label: 'My Listings' },
  { key: 'myTeam', label: 'Team Listings' },
  { key: 'others', label: 'Other Teams\' Listings' },
  { key: 'search', label: 'Search Listings' }
];

/** Indicates the page number of the search page. We start counting at 1. */
const SEARCH_PAGE_NUMBER = 1 + VIEWING_LIST_PROPERTY_SELECTION_GROUPS.findIndex(
  group => group.key === 'search'
);

export class VlPropertySelectionPanel extends Component<VlPropertySelectionPanelProps, VlPropertySelectionPanelState> {
  sliderRef = createRef<HTMLDivElement>();
  searchCardContainerRef = createRef<HTMLDivElement>();

  constructor(props) {
    super(props);

    this.state = {
      currentPage: 1,
    }
  }

  onCardSliderScroll(event: UIEvent) {
    const element = event.srcElement as HTMLDivElement;
    const currentPage = Math.floor((element.scrollLeft / element.clientWidth) + .5) + 1;

    if (this.state.currentPage !== currentPage) {
      this.setState({ currentPage });
      if (this.props.onPageChange) {
        this.props.onPageChange(currentPage);
      }
    }
  }

  onDesktopSearchContainerScroll(event: Event) {
    const scroller = event.target as HTMLDivElement;
    const searchScrollPos = scroller.scrollTop;
    const lastScrollPos = this.state.searchScrollPos;

    const farEnoughFromTop = searchScrollPos > 20;
    const scrolledDown = searchScrollPos > lastScrollPos;

    const searchHiddenByScroll = farEnoughFromTop && scrolledDown;
    this.setState({ searchScrollPos, searchHiddenByScroll });
  }

  scrollToSlideIndex(index: number) {
    const slider = this.sliderRef.current as HTMLDivElement;
    // This component standardizes its nmbering to start at 1, which happens
    // to be how CSS does its indexing as well. No addition necessary here.
    const slide = slider.querySelector<HTMLDivElement>(`.hi-vlmselect__card-slide:nth-child(${ index })`);

    slider.scroll({
      top: 0,
      left: slide.offsetLeft,
      behavior: 'smooth'
    });
  }

  isSearchButtonVisible() {
    const state = this.state;
    return state.currentPage === SEARCH_PAGE_NUMBER && !state.searchHiddenByScroll;
  }

  getSearchTabContent() {
    const props = this.props;
    const { propertySearchResults, viewingList } = props;
    const savedSearchListings = props.viewingList.search.results || [];

    const listings = (propertySearchResults && propertySearchResults.results) || savedSearchListings;

    const cards = listings ? listings.filter(
      listing => !viewingList.finalList.keys.includes(listing.propertyKey)
    ).map(listing => (
      <InqPropertyCard key={ `${listing.propertyKey}-search` }
        listing={ listing }
        onAddToListClick={ () => props.onAddToList(listing, 'search') } />
    )) : null;

    return (
      <div className="d-flex flex-column">
        <button className="btn hi-vlmselect__open-search-button hi-vlmselect__open-search-button--mobile"
          onClick={ this.props.onSearch }>
          <i className="fas fa-search" /> Search
        </button>

        { cards }
      </div>
    );
  }

  getRegularTabContent(groupKey: string): JSX.Element {
    const viewingList = this.props.viewingList;
    const group = viewingList[groupKey] as ViewingListPropertyGroup;

    if (group.count > 0) {
      return viewingList[groupKey].results.filter(
        listing => !viewingList.finalList.keys.includes(listing.propertyKey)
      ).map(listing => (
        <InqPropertyCard
          key={ listing.propertyKey }
          listing={ listing }
          onAddToListClick={ () => this.props.onAddToList(listing, groupKey) } />
      ));
    } else {
      return (
        <span className="hi-vlmselect__no-listings-msg">
          No listings under this category.
        </span>
      );
    }
  }

  getTabContent(groupKey: string, groupName: string, index: number) {
    const props = this.props;
    let ref: RefObject<HTMLDivElement> = null;
    let scrollHandler: (event: Event) => void = null;
    let inSearch = groupKey === 'search';

    if (inSearch) {
      ref = this.searchCardContainerRef;
      scrollHandler = event => this.onDesktopSearchContainerScroll(event);
    }

    return (
      <div className="hi-vlmselect__card-slide" ref={ ref } onScroll={ scrollHandler }>
        <div className="d-lg-none hi-vlmselect__sticky-header">
          <VlPropertySelectionHeader
            groupName={ groupName }
            numGroups={ VIEWING_LIST_PROPERTY_SELECTION_GROUPS.length }
            currentPage={ index + 1 }
            onPageSelect={ page => this.scrollToSlideIndex(page) }
            onViewFtf={ props.onViewFtf } />
        </div>
        <div className="hi-vlmselect__card-list">
          { groupKey == 'search' ? this.getSearchTabContent() : this.getRegularTabContent(groupKey) }
        </div>
      </div>
    )
  }

  render() {
    const props = this.props;
    const state = this.state;
    const viewingList = props.viewingList;

    return (
      <div className="hi-vlmselect">
        {/* Title bar */}
        <div className="hi-vlmselect__title-bar">
          <span className="hi-vlmselect__title-label">
            Create Viewing List
          </span>

          <button className="btn btn-link hi-vlmselect__title-close" onClick={ props.onClose }>
            <i className="fas fa-times" /> Close
          </button>
        </div>

        {/* Header */}
        <div className="d-none d-lg-block">
          <VlPropertySelectionHeader
            groupName={ VIEWING_LIST_PROPERTY_SELECTION_GROUPS[state.currentPage - 1].label }
            numGroups={ VIEWING_LIST_PROPERTY_SELECTION_GROUPS.length }
            currentPage={ state.currentPage }
            onPageSelect={ page => this.scrollToSlideIndex(page) }
            onViewFtf={ props.onViewFtf } />
        </div>

        <button
          className={
            cx("btn hi-vlmselect__open-search-button hi-vlmselect__open-search-button--desktop", {
              'hi-vlmselect__open-search-button--open': this.isSearchButtonVisible()
            })
          }
          onClick={ this.props.onSearch }
          disabled={ state.currentPage !== SEARCH_PAGE_NUMBER }>
          {/*
            There's a brief moment where the search button is clickable but
            we've already left the search page. We want the search panel to only
            be openable when we're in the search page, so we have to explicitly
            disable this button.
          */}
          <i className="fas fa-search" /> Search
        </button>
        

        <div className="hi-vlmselect__card-slider-container">
          {/*
            Take note that this implementation does not have an inner "slides"
            container, unlike the inquiry grid.
          */}
          <div className="hi-vlmselect__card-slider"
            ref={ this.sliderRef }
            onScroll={ e => this.onCardSliderScroll(e) }
          >
            { VIEWING_LIST_PROPERTY_SELECTION_GROUPS.map(
              (group, index) => this.getTabContent(group.key, group.label, index)
            )}
          </div>
        </div>

        {/* View list button row */}
        <div className="hi-vlmselect__view-list-button-row">
          <button className="btn btn-secondary hi-vlmselect__view-list-button"
            onClick={ () => this.props.onOpenFinalList() }>
            View list ({ viewingList.finalList.results.length }) <i className="fas fa-arrow-right" />
          </button>
        </div>
      </div>
    );
  }

  componentWillReceiveProps(nextProps: VlPropertySelectionPanelProps) {
    if (nextProps.propertySearchResults !== this.props.propertySearchResults) {
      const ref = this.searchCardContainerRef;
      let element: HTMLDivElement;
      // if (ref.current) {
      //   this.searchCardContainerRef.current.scrollTo(0, 0);
      // }
    }
  }
}
